<template>
  <SessionModal :open="open" size="sm" :title="''" @close="$emit('close')">
    <div class="space-y-6">
      <!-- Warning Message -->
      <div class="text-start mt-6">
        <h3 class="text-sm font-semibold text-red-600 mb-1">WARNING</h3>
        <h1 class="text-2xl font-bold text-gray-900 mb-1">Cancel Session</h1>
        <p class="text-sm text-gray-500">
          This action cannot be undone and will notify the {{ isTutorView ? 'student' : 'tutor' }}.
        </p>
      </div>

      <!-- Session Details -->
      <div class="bg-white p-4 rounded-lg border border-gray-200">
        <h3 class="text-sm font-medium text-gray-900 mb-4">Session Details</h3>

        <!-- Tutor/Student Section -->
        <div class="mb-3">
          <div class="flex items-center gap-2">
            <UserIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p class="text-xs text-gray-500 font-medium w-16">{{ isTutorView ? 'Student' : 'Tutor' }}:</p>
            <p class="text-sm font-semibold text-gray-900">{{ session?.tutorName }}</p>
          </div>
        </div>

        <!-- Subject Section -->
        <div class="mb-3">
          <div class="flex items-center gap-2 min-w-0">
            <BookOpenIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p class="text-xs text-gray-500 font-medium w-16 flex-shrink-0">Subject:</p>
            <p class="text-sm font-semibold text-gray-900 truncate" :title="session?.subject">{{ session?.subject }}</p>
          </div>
        </div>

        <!-- Date Section -->
        <div class="mb-3">
          <div class="flex items-center gap-2">
            <CalendarIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p class="text-xs text-gray-500 font-medium w-16">Date:</p>
            <p class="text-sm font-semibold text-gray-900">
              {{ formatDate(session?.date || "") }}
            </p>
          </div>
        </div>

        <!-- Time Section -->
        <div class="mb-3">
          <div class="flex items-center gap-2">
            <ClockIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
            <p class="text-xs text-gray-500 font-medium w-16">Time:</p>
            <p class="text-sm font-semibold text-gray-900">{{ session?.time }}</p>
          </div>
        </div>

        <!-- Mode/Location Section -->
        <div class="flex items-center gap-2">
          <VideoCameraIcon
            v-if="session?.mode === 'online'"
            class="h-4 w-4 text-gray-400 flex-shrink-0"
          />
          <MapPinIcon v-else class="h-4 w-4 text-gray-400 flex-shrink-0" />
          <p class="text-xs text-gray-500 font-medium w-16">Mode:</p>
          <p class="text-sm font-semibold text-gray-900">
            <span v-if="session?.mode === 'face-to-face'" class="font-semibold">Face-to-face - {{ session?.location || "Location TBD" }}</span>
            <span v-else-if="session?.mode === 'online'" class="font-semibold">Online Class</span>
            <span v-else class="font-semibold">{{ session?.mode }} - {{ session?.location || "No location" }}</span>
          </p>
        </div>
      </div>

      <!-- No-Show Warning (shown when canceling after session start) -->
      <div v-if="isNoShowScenario" class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5 text-yellow-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p class="text-sm font-medium text-yellow-800">
            Session has started. This cancellation will be marked as a no-show.
          </p>
        </div>
      </div>

      <!-- Cancellation Reason -->
      <div>
        <label for="cancelReason" class="block text-sm font-medium text-gray-700 mb-2">
          Reason for Cancellation <span class="text-red-500">*</span>
        </label>
        <select
          id="cancelReason"
          v-model="selectedReason"
          class="block w-full px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-900 leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
          required
        >
          <option value="">Select a reason</option>
          <!-- Show only No-Show and Other when canceling 15+ minutes after session start -->
          <template v-if="isNoShowScenario">
            <option value="no-show">No Show</option>
            <option value="other">Other</option>
          </template>
          <!-- Show all options for regular cancellations -->
          <template v-else>
            <option value="schedule-conflict">Schedule Conflict</option>
            <option value="emergency">Emergency</option>
            <option value="illness">Illness</option>
            <option value="technical-issues">Technical Issues</option>
            <option value="personal-matter">Personal Matter</option>
            <option value="tutor-request">{{ isTutorView ? 'Student Request' : 'Tutor Request' }}</option>
            <option value="other">Other</option>
          </template>
        </select>
      </div>

      <!-- Additional Notes for "Other" reason -->
      <div v-if="selectedReason === 'other'">
        <label for="otherReason" class="block text-sm font-medium text-gray-700 mb-2">
          Please specify <span class="text-red-500">*</span>
        </label>
        <textarea
          id="otherReason"
          v-model="otherReason"
          rows="3"
          placeholder="Please provide details about your cancellation reason..."
          class="block w-full px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-900 leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
          required
        ></textarea>
      </div>

      <!-- Additional Notes (Optional) -->
      <div v-if="selectedReason && selectedReason !== 'other'">
        <label for="additionalNotes" class="block text-sm font-medium text-gray-700 mb-2">
          Additional Notes (Optional)
        </label>
        <textarea
          id="additionalNotes"
          v-model="additionalNotes"
          rows="2"
          placeholder="Any additional information..."
          class="block w-full px-3 py-2 border border-gray-300 text-sm rounded-md text-gray-900 leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
        ></textarea>
      </div>
    </div>

    <div class="flex gap-4 mb-3" style="margin-top: 1.5rem">
      <button
        type="button"
        @click="$emit('close')"
        class="flex-1 px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
      >
        Keep Session
      </button>
      <button
        type="button"
        @click="handleCancel"
        :disabled="
          isSubmitting || !selectedReason || (selectedReason === 'other' && !otherReason.trim())
        "
        class="flex-1 px-6 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span v-if="isSubmitting">Canceling...</span>
        <span v-else>Cancel Session</span>
      </button>
    </div>
  </SessionModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import SessionModal from "@/components/Student-Tutor/SessionModal.vue";
import {
  BookOpenIcon,
  CalendarIcon,
  ClockIcon,
  PencilSquareIcon,
  UserIcon,
  MapPinIcon,
  VideoCameraIcon,
} from "@heroicons/vue/24/outline";
import type { UpcomingSession } from "@/types";

interface Props {
  open: boolean;
  session?: UpcomingSession;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  cancel: [data: { sessionId: string; reason: string; cancelledBy: string; cancelledAt: Date }];
}>();

const selectedReason = ref("");
const otherReason = ref("");
const additionalNotes = ref("");
const isSubmitting = ref(false);

// Check if this is being used by a tutor (has studentId in session)
const isTutorView = computed(() => {
  return props.session?.studentId !== undefined;
});

// Check if this is a no-show scenario (15+ minutes after session start)
const isNoShowScenario = computed(() => {
  if (!props.session?.startAt) return false;
  
  // Parse the session start time
  let sessionStartTime: Date;
  
  // Handle different startAt formats
  if (props.session.startAt instanceof Date) {
    sessionStartTime = props.session.startAt;
  } else if ((props.session.startAt as any)?.toDate) {
    // Firestore Timestamp
    sessionStartTime = (props.session.startAt as any).toDate();
  } else if (props.session.date && props.session.time) {
    // Construct from date and time strings
    sessionStartTime = new Date(`${props.session.date} ${props.session.time}`);
  } else {
    return false;
  }
  
  const now = new Date();
  const minutesSinceStart = (now.getTime() - sessionStartTime.getTime()) / (1000 * 60);
  
  // Return true if session started 15+ minutes ago
  return minutesSinceStart >= 15;
});

// Memoized date formatting for better performance
const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Reset form when modal closes
const resetForm = () => {
  selectedReason.value = "";
  otherReason.value = "";
  additionalNotes.value = "";
  isSubmitting.value = false;
};

// Watch for modal close to reset form and lock body scroll
watch(
  () => props.open,
  (newValue, oldValue) => {
    if (newValue) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      resetForm();
    }
  },
  { immediate: true }
);

const handleCancel = async () => {
  if (!props.session || isSubmitting.value) return;

  // Validate required fields
  if (!selectedReason.value) {
    alert("Please select a cancellation reason.");
    return;
  }

  if (selectedReason.value === "other" && !otherReason.value.trim()) {
    alert("Please provide details for your cancellation reason.");
    return;
  }

  isSubmitting.value = true;

  try {
    // Prepare the reason text
    let reasonText = selectedReason.value;
    if (selectedReason.value === "other") {
      reasonText = `Other: ${otherReason.value}`;
    }
    if (additionalNotes.value.trim()) {
      reasonText += ` | Additional Notes: ${additionalNotes.value}`;
    }

    emit("cancel", {
      sessionId: props.session.id,
      reason: reasonText,
      cancelledBy: "student", // Since this is the student canceling
      cancelledAt: new Date(),
    });
  } catch (error) {
    // Error handling for session cancellation
  } finally {
    isSubmitting.value = false;
  }
};
</script>
